import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ILoginCredentials } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import Button from '../common/Button';
import Input from '../common/Input';
import toast from 'react-hot-toast';
import { AuthService } from '../../services/auth.service';

const LoginForm: React.FC = () => {
    const navigate = useNavigate();
    const { login, logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [credentials, setCredentials] = useState<ILoginCredentials>({
        email: '',
        password: ''
    });
    const [error, setError] = useState<string>('');
    const [mfaCode, setMfaCode] = useState<string>(''); // State for MFA code
    const [requiresMfa, setRequiresMfa] = useState<boolean>(false); // State to track MFA requirement
    const [step, setStep] = useState<number>(1); // State to track the current step
    const [tempToken, setTempToken] = useState<string>('');
    const [showMfaHelp, setShowMfaHelp] = useState<boolean>(false);
    const [adminMessage, setAdminMessage] = useState<string>('');
    const [sendingRequest, setSendingRequest] = useState<boolean>(false);
    
    // Security-related states
    const [securityAlert, setSecurityAlert] = useState<{
        show: boolean;
        failedAttempts: number;
        accountLocked: boolean;
        message: string;
    }>({
        show: false,
        failedAttempts: 0,
        accountLocked: false,
        message: ''
    });
    const [showLockAccountModal, setShowLockAccountModal] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCredentials(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
        // Clear error when user starts typing
        if (error) setError('');
        // Clear security alert when user starts typing
        if (securityAlert.show) {
            setSecurityAlert({ show: false, failedAttempts: 0, accountLocked: false, message: '' });
        }
    };


    const handleMfaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6)); // Allow only 6 digits
    };

    const queueRsaMigrationIfNeeded = async () => {
        // We only trigger the migration banner for V1 users
        const { deriveKeys } = await import('../../utils/encryption');
        const { encryptionKey: derivedKey } = deriveKeys(credentials.password, credentials.email);

        sessionStorage.setItem('rsa_migration_pending', 'true');
        sessionStorage.setItem('rsa_migration_email', credentials.email);
        sessionStorage.setItem('rsa_migration_encryption_key', derivedKey);
        sessionStorage.setItem('rsa_migration_show_time', Date.now().toString());

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('rsa-migration-flag-updated'));
        }
    };

    const maybeScheduleRsaMigration = async (cryptoVersion?: number) => {
        if (cryptoVersion === 1) {
            console.log('🔒 User has crypto_version=1, queuing RSA migration modal');
            await queueRsaMigrationIfNeeded();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        // Basic validation
        if (!credentials.email || !credentials.password) {
            setError('Please fill in all fields');
            setLoading(false);
            return;
        }

        try {
            // Check if this user was previously marked as new
            const isNewUser = localStorage.getItem('isNewUser') === 'true';
            
            // Login will handle the loading state internally
            const result = await login(credentials);
            
            if (result.success) {
                setLoading(false);
                
                // If the user was marked as new before, preserve that status
                // This ensures the onboarding tour will show after login for new users
                if (isNewUser) {
                    localStorage.setItem('isNewUser', 'true');
                }
                
                await maybeScheduleRsaMigration(result.cryptoVersion);
                
                // Always navigate immediately (route guard will redirect to /vaults or let through to /all-items)
                navigate('/all-items');
            } else if (result.requiresMfa) {
                setLoading(false);
                setRequiresMfa(true);
                setStep(2);
                setTempToken(result.tempToken || '');
            } else {
                setLoading(false);
                
                // Handle security-related responses
                if (result.accountLocked) {
                    setSecurityAlert({
                        show: true,
                        failedAttempts: result.failedAttempts || 0,
                        accountLocked: true,
                        message: result.error || result.message || 'Account is locked'
                    });
                    setError('');
                } else if (result.securityAlert) {
                    setSecurityAlert({
                        show: true,
                        failedAttempts: result.failedAttempts || 0,
                        accountLocked: false,
                        message: result.error || result.message || 'Security alert'
                    });
                    setError('');
                    
                    // Show option to lock account if this is the second failed attempt
                    if (result.failedAttempts === 2) {
                        toast.error('Multiple failed attempts detected. Consider locking your account for security.');
                    }
                } else if (result.failedAttempts) {
                    // Show failed attempts count for first attempt
                    setError(`${result.error || 'Invalid credentials'} (Attempt ${result.failedAttempts}/3)`);
                } else {
                    setError(result.error || 'Invalid credentials');
                }
            }
        } catch (err: any) {
            setLoading(false);
            setError(err.message || 'Login failed');
        }
    };

    // Handle self-locking account
    const handleLockAccount = async () => {
        if (!credentials.email) {
            toast.error('Please enter your email address first');
            return;
        }

        setLoading(true);
        try {
            const result = await AuthService.lockMyAccount(credentials.email);
            if (result.success) {
                toast.success(result.message);
                setShowLockAccountModal(false);
                setSecurityAlert({
                    show: true,
                    failedAttempts: 0,
                    accountLocked: true,
                    message: 'Account locked for security. Check your email for unlock instructions.'
                });
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to lock account');
        } finally {
            setLoading(false);
        }
    };

    // Render security alert component
    const renderSecurityAlert = () => {
        if (!securityAlert.show) return null;

        return (
            <div className={`p-4 rounded-lg border mb-4 ${
                securityAlert.accountLocked 
                    ? 'bg-red-50 border-red-200 text-red-800' 
                    : 'bg-yellow-50 border-yellow-200 text-yellow-800'
            }`}>
                <div className="flex items-start">
                    <div className={`flex-shrink-0 w-5 h-5 mt-0.5 mr-3 ${
                        securityAlert.accountLocked ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                        {securityAlert.accountLocked ? (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        )}
                    </div>
                    <div className="flex-1">
                        <h3 className={`text-sm font-medium ${
                            securityAlert.accountLocked ? 'text-red-800' : 'text-yellow-800'
                        }`}>
                            {securityAlert.accountLocked ? '🔒 Account Locked' : '⚠️ Security Alert'}
                        </h3>
                        <p className={`mt-1 text-sm ${
                            securityAlert.accountLocked ? 'text-red-700' : 'text-yellow-700'
                        }`}>
                            {securityAlert.message}
                        </p>
                        
                        {!securityAlert.accountLocked && securityAlert.failedAttempts > 0 && (
                            <div className="mt-2">
                                <p className="text-sm text-yellow-700">
                                    Failed attempts: <strong>{securityAlert.failedAttempts}/3</strong>
                                </p>
                            </div>
                        )}
                        
                        {securityAlert.accountLocked && (
                            <div className="mt-2">
                                <p className="text-sm text-red-700">
                                    Check your email for account recovery instructions.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render lock account confirmation modal
    const renderLockAccountModal = () => {
        if (!showLockAccountModal) return null;

        return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                    <div className="mt-3 text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">Lock My Account</h3>
                        <div className="mt-2 px-7 py-3">
                            <p className="text-sm text-gray-500">
                                Are you sure you want to lock your account for security? 
                                You'll receive an email with instructions to unlock it.
                            </p>
                        </div>
                        <div className="items-center px-4 py-3">
                            <button
                                onClick={handleLockAccount}
                                disabled={loading}
                                className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                            >
                                {loading ? 'Locking...' : 'Yes, Lock My Account'}
                            </button>
                            <button
                                onClick={() => setShowLockAccountModal(false)}
                                className="mt-3 px-4 py-2 bg-white text-gray-500 text-base font-medium rounded-md w-full shadow-sm border border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleMfaSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        if (!mfaCode) {
            setError('Please enter the MFA code');
            setLoading(false);
            return;
        }

        try {
            // Check if this user was previously marked as new
            const isNewUser = localStorage.getItem('isNewUser') === 'true';
            
            console.log("MFA code", mfaCode);
            const result = await login({
                ...credentials,
                mfaCode,
                tempToken: tempToken // Use the stored tempToken
            });
            console.log("MFA result", result);

            if (result.success) {
                setLoading(false);
                
                // If the user was marked as new before, preserve that status
                // This ensures the onboarding tour will show after MFA verification for new users
                if (isNewUser) {
                    localStorage.setItem('isNewUser', 'true');
                }
                
                await maybeScheduleRsaMigration(result.cryptoVersion);
                
                navigate('/all-items');
            } else {
                setLoading(false);
                setError(result.error || 'Invalid MFA code');
            }
        } catch (err: any) {
            setLoading(false);
            setError(err.message || 'MFA verification failed');
        }
    };

    const handleSendAdminRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setSendingRequest(true);
        
        try {
            if (!adminMessage.trim()) {
                setError('Please provide a message for the administrator');
                setSendingRequest(false);
                return;
            }
            
            // Use AuthService instead of direct API call
            const response = await AuthService.requestMfaHelp(credentials.email, adminMessage);
            
            if (response.success) {
                toast.success('Your request has been sent to the administrator');
                setShowMfaHelp(false);
                setAdminMessage('');
            } else {
                toast.error(response.message || 'Failed to send request');
            }
        } catch (error: any) {
            console.error('Error sending admin request:', error);
            toast.error(error.message || 'An error occurred while sending your request');
        } finally {
            setSendingRequest(false);
        }
    };

    return (
        <form onSubmit={step === 1 ? handleSubmit : (showMfaHelp ? handleSendAdminRequest : handleMfaSubmit)} className="space-y-6">
            {/* Security Alert Component */}
            {renderSecurityAlert()}
            
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}
            
            {step === 1 ? (
                <>
                    <Input
                        label="Email address"
                        type="email"
                        name="email"
                        value={credentials.email}
                        onChange={handleChange}
                        required
                        autoFocus
                        disabled={loading}
                    />

                    <Input
                        label="Master Password"
                        type="password"
                        name="password"
                        value={credentials.password}
                        onChange={handleChange}
                        required
                        showPasswordToggle
                        disabled={loading}
                    />
                    <div className="flex justify-between">
                        <div className="text-sm">
                            <Link
                                to="/forgot-password"
                                className={`font-medium text-blue-600 hover:text-blue-500 ${loading ? 'pointer-events-none opacity-50' : ''}`}
                            >
                                Forgot your password?
                            </Link>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {!showMfaHelp ? (
                        <>
                            <h3 className="text-lg font-medium mb-2">Two-factor authentication</h3>
                            <p className="text-sm text-gray-600 mb-2">
                                Open your two-factor authenticator (TOTP) app or browser extension to view your authentication code.
                            </p>
                            <Input
                                label="Authentication code"
                                type="text"
                                value={mfaCode}
                                onChange={handleMfaChange}
                                placeholder="XXXXXX"
                                maxLength={6}
                                required
                            />
                            <div className="text-sm mt-4">
                                <button 
                                    type="button"
                                    onClick={() => setShowMfaHelp(true)}
                                    className="font-medium text-blue-600 hover:text-blue-500 focus:outline-none"
                                >
                                    Can't access your authenticator app?
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 className="text-lg font-medium mb-2">Request admin help</h3>
                            <p className="text-sm text-gray-600 mb-2">
                                Contact your admin to temporarily disable two-factor authentication for your account.
                            </p>
                            <textarea
                                value={adminMessage}
                                onChange={(e) => setAdminMessage(e.target.value)}
                                placeholder="Please explain why you need help accessing your account..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                rows={4}
                                required
                            />
                            <div className="text-sm mt-4">
                                <button 
                                    type="button"
                                    onClick={() => setShowMfaHelp(false)}
                                    className="font-medium text-blue-600 hover:text-blue-500 focus:outline-none"
                                >
                                    Back to authentication code
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}

            {error && step !== 1 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            <Button
                type="submit"
                variant="primary"
                loading={loading || sendingRequest}
                disabled={loading || sendingRequest || securityAlert.accountLocked}
                className="w-full"
            >
                {step === 1 
                    ? (loading ? 'Signing in...' : 'Sign in')
                    : showMfaHelp 
                        ? (sendingRequest ? 'Sending request...' : 'Send admin request')
                        : (loading ? 'Verifying...' : 'Verify code')
                }
            </Button>

            {/* Lock Account Modal */}
            {renderLockAccountModal()}
        </form>
    );
};


export default LoginForm;